import * as React from "react";
import { cn } from "@/lib/utils";

interface ProgressBarProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Number of items completed. */
  value: number;
  /** Total number of items. */
  max: number;
  /** Accessible label for the progress bar (defaults to "Progress"). */
  label?: string;
  /** Hide the textual summary below the bar. */
  hideText?: boolean;
}

/**
 * Accessible progress bar that displays both a visual fill and a textual
 * "X of Y completed" summary. Uses the correct ARIA progressbar role
 * and attributes.
 */
const ProgressBar = React.forwardRef<HTMLDivElement, ProgressBarProps>(
  ({ value, max, label = "Progress", hideText = false, className, ...props }, ref) => {
    const clamped = Math.max(0, Math.min(value, max));
    const percentage = max > 0 ? Math.round((clamped / max) * 100) : 0;

    return (
      <div ref={ref} className={cn("flex flex-col gap-1.5", className)} {...props}>
        {/* Track */}
        <div
          role="progressbar"
          aria-label={label}
          aria-valuenow={clamped}
          aria-valuemin={0}
          aria-valuemax={max}
          className="h-2.5 w-full overflow-hidden rounded-full bg-line"
        >
          {/* Fill */}
          <div
            className="h-full rounded-full bg-primary transition-all duration-500 ease-out"
            style={{ width: `${percentage}%` }}
          />
        </div>

        {/* Textual summary */}
        {!hideText && (
          <p className="text-xs font-medium text-mid">
            {percentage}% &mdash; {clamped} of {max} completed
          </p>
        )}
      </div>
    );
  },
);

ProgressBar.displayName = "ProgressBar";

export { ProgressBar };
export type { ProgressBarProps };
